"use client";

import { Market } from "@features/market/config/i18n-config";
import type { Auth, User } from "firebase/auth";
import {
  createContext,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import useRefreshToken from "../hooks/useRefreshToken";
import initAuthClient from "../services/initAuthClient";

export type AuthContextValue = {
  auth: Auth | undefined;
  user: User | null;
  token: string | undefined;
  authenticated: boolean;
  loading: boolean;
  initializing: boolean;
  refreshing: boolean;
  authenticateUser: (user: User) => Promise<unknown>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export function AuthProvider({
  children,
  market,
}: PropsWithChildren<{ market: Market }>) {
  const [auth, setAuth] = useState<Auth | undefined>(undefined);

  useEffect(() => {
    initAuthClient(market).then(setAuth);
  }, [market]);

  const {
    user,
    token,
    authenticated,
    loading,
    initializing,
    refreshing,
    authenticate,
  } = useRefreshToken({
    auth,
    serviceLocale: market.locale,
  });

  const value = useMemo(
    () => ({
      auth,
      user,
      token,
      authenticated,
      loading,
      initializing,
      refreshing,
      authenticateUser: authenticate,
    }),
    [
      auth,
      user,
      token,
      authenticated,
      loading,
      initializing,
      refreshing,
      authenticate,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
